import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { 
  Play, Pause, Plus, Trash2, Video, Clock, DollarSign, 
  ChevronLeft, Save, Loader2, Settings, Target, Link2,
  SkipBack, SkipForward, Volume2, VolumeX, Maximize,
  AlertCircle, CheckCircle, Edit2, X
} from 'lucide-react';
import axios from 'axios';
import Hls from 'hls.js';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Helper function to convert video URL/ID to playable HLS URL
const getPlayableVideoUrl = (videoData) => {
  if (!videoData) return '';
  
  // Priority: r2_url_4k > r2_url_1080p > stream_url > playback_url > hls_url > cloudflare_video_id > cloudflare_uid > video_url
  const videoUrl = videoData.r2_url_4k || 
                   videoData.r2_url_1080p || 
                   videoData.stream_url || 
                   videoData.playback_url || 
                   videoData.hls_url || 
                   videoData.cloudflare_video_id ||
                   videoData.cloudflare_uid ||
                   videoData.video_url || 
                   '';
  
  console.log('[AdCueEditor] Raw video fields:', {
    r2_url_4k: videoData.r2_url_4k,
    r2_url_1080p: videoData.r2_url_1080p,
    stream_url: videoData.stream_url,
    playback_url: videoData.playback_url,
    hls_url: videoData.hls_url,
    cloudflare_video_id: videoData.cloudflare_video_id,
    cloudflare_uid: videoData.cloudflare_uid,
    video_url: videoData.video_url,
    selected: videoUrl
  });
  
  if (!videoUrl) return '';
  
  // If it's already a full URL (R2 or HLS), use as-is
  if (videoUrl.startsWith('https://')) {
    // If it's a cloudflarestream URL, convert to videodelivery.net for better compatibility
    if (videoUrl.includes('cloudflarestream.com')) {
      const match = videoUrl.match(/\/([a-f0-9]{32})\//);
      if (match) {
        return `https://videodelivery.net/${match[1]}/manifest/video.m3u8`;
      }
    }
    // If it's a customer-cloudflarestream URL with UID in path
    if (videoUrl.includes('customer-') && videoUrl.includes('cloudflarestream.com')) {
      const match = videoUrl.match(/\/([a-f0-9]{32})/);
      if (match) {
        return `https://videodelivery.net/${match[1]}/manifest/video.m3u8`;
      }
    }
    return videoUrl;
  }
  
  // If it's a 32-character hex string (Cloudflare Stream ID), convert to HLS URL
  if (videoUrl.length === 32 && /^[a-f0-9]+$/i.test(videoUrl)) {
    return `https://videodelivery.net/${videoUrl}/manifest/video.m3u8`;
  }
  
  // Return as-is for other formats
  return videoUrl;
};

const AdCuePointEditor = ({ authToken, videoId, onBack }) => {
  const videoRef = useRef(null);
  const timelineRef = useRef(null);
  const hlsRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [videoData, setVideoData] = useState(null);
  const [adSettings, setAdSettings] = useState(null);
  const [cuePoints, setCuePoints] = useState([]);
  const [videoError, setVideoError] = useState(null);
  const [availableAds, setAvailableAds] = useState([]);
  
  // Video player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  
  // Edit state
  const [editingCue, setEditingCue] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCue, setNewCue] = useState({ 
    timestamp: 0, 
    ad_type: 'google', 
    custom_ad_id: '', 
    custom_ad_url: '', 
    custom_ad_duration: 15,
    // CTA settings - stored directly in cue point
    skip_after: 5,
    cta_enabled: false,
    cta_text: 'Learn More',
    click_url: ''
  });
  
  // Fetch available ads for dropdown
  const fetchAvailableAds = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${authToken}` };
      const res = await axios.get(`${API}/api/vod-admin/ad-cues/available-ads`, { headers });
      setAvailableAds(res.data.ads || []);
    } catch (err) {
      console.error('Error fetching available ads:', err);
    }
  }, [authToken]);
  
  // Fetch video and ad settings
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${authToken}` };
      const res = await axios.get(`${API}/api/vod-admin/ad-cues/video/${videoId}`, { headers });
      setVideoData(res.data.video);
      setAdSettings(res.data.ad_settings);
      setCuePoints(res.data.ad_settings?.cue_points || []);
    } catch (err) {
      console.error('Error fetching video data:', err);
    } finally {
      setLoading(false);
    }
  }, [authToken, videoId]);
  
  useEffect(() => {
    if (authToken && videoId) {
      fetchData();
      fetchAvailableAds();
    }
  }, [authToken, videoId, fetchData, fetchAvailableAds]);
  
  // Initialize HLS.js when video data is available
  useEffect(() => {
    if (!videoData || !videoRef.current) return;
    
    const videoUrl = getPlayableVideoUrl(videoData);
    console.log('[AdCueEditor] Video URL:', videoUrl);
    
    if (!videoUrl) {
      setVideoError('No video URL available');
      return;
    }
    
    // Cleanup previous HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    
    // Check if it's an HLS stream (.m3u8)
    const isHls = videoUrl.includes('.m3u8') || videoUrl.includes('videodelivery.net');
    
    if (isHls && Hls.isSupported()) {
      // Use HLS.js for HLS streams
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 90
      });
      
      hls.loadSource(videoUrl);
      hls.attachMedia(videoRef.current);
      
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('[AdCueEditor] HLS manifest loaded');
        setVideoError(null);
      });
      
      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('[AdCueEditor] HLS error:', data);
        if (data.fatal) {
          setVideoError(`Video load error: ${data.type}`);
        }
      });
      
      hlsRef.current = hls;
    } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS support
      videoRef.current.src = videoUrl;
      setVideoError(null);
    } else {
      // Direct video URL (MP4, etc.)
      videoRef.current.src = videoUrl;
      setVideoError(null);
    }
    
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [videoData]);
  
  // Video event handlers
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };
  
  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };
  
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };
  
  const seekTo = (time) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };
  
  const skip = (seconds) => {
    if (videoRef.current) {
      const newTime = Math.max(0, Math.min(duration, currentTime + seconds));
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };
  
  // Timeline click handler
  const handleTimelineClick = (e) => {
    if (timelineRef.current && duration > 0) {
      const rect = timelineRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = clickX / rect.width;
      const newTime = percentage * duration;
      seekTo(newTime);
    }
  };
  
  // Add cue point at current time
  const addCueAtCurrentTime = () => {
    setNewCue({
      timestamp: currentTime,
      ad_type: 'google',
      custom_ad_url: '',
      custom_ad_duration: 15
    });
    setShowAddModal(true);
  };
  
  // Save new cue point
  const saveCuePoint = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${authToken}` };
      console.log('[AdCuePointEditor] Saving cue point with CTA settings:', newCue);
      const res = await axios.post(`${API}/api/vod-admin/ad-cues/video/${videoId}/cue-point`, newCue, { headers });
      setCuePoints([...cuePoints, res.data.cue_point]);
      setShowAddModal(false);
      // Reset form with all fields including CTA settings
      setNewCue({ 
        timestamp: 0, 
        ad_type: 'google', 
        custom_ad_id: '', 
        custom_ad_url: '', 
        custom_ad_duration: 15,
        skip_after: 5,
        cta_enabled: false,
        cta_text: 'Learn More',
        click_url: ''
      });
    } catch (err) {
      console.error('Error saving cue point:', err);
      alert('Failed to save cue point');
    } finally {
      setSaving(false);
    }
  };
  
  // Delete cue point
  const deleteCuePoint = async (cueId) => {
    if (!window.confirm('Delete this ad cue point?')) return;
    
    try {
      const headers = { Authorization: `Bearer ${authToken}` };
      await axios.delete(`${API}/api/vod-admin/ad-cues/video/${videoId}/cue-point/${cueId}`, { headers });
      setCuePoints(cuePoints.filter(c => c.id !== cueId));
    } catch (err) {
      console.error('Error deleting cue point:', err);
      alert('Failed to delete cue point');
    }
  };
  
  // Update ad settings (pre-roll, post-roll, mode)
  const updateSettings = async (updates) => {
    try {
      const headers = { Authorization: `Bearer ${authToken}` };
      await axios.put(`${API}/api/vod-admin/ad-cues/video/${videoId}/settings`, updates, { headers });
      setAdSettings({ ...adSettings, ...updates });
    } catch (err) {
      console.error('Error updating settings:', err);
      alert('Failed to update settings');
    }
  };
  
  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }
  
  if (!videoData) {
    return (
      <div className="text-center py-20">
        <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
        <p className="text-gray-400">Video not found</p>
        <Button onClick={onBack} className="mt-4">Go Back</Button>
      </div>
    );
  }
  
  const sortedCuePoints = [...cuePoints].sort((a, b) => a.timestamp - b.timestamp);
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack} className="text-gray-400">
            <ChevronLeft className="h-5 w-5 mr-1" />
            Back to Videos
          </Button>
          <div>
            <h2 className="text-xl font-bold text-white">{videoData.title}</h2>
            <p className="text-sm text-gray-400">Ad Cue Point Editor</p>
          </div>
        </div>
      </div>
      
      {/* Main Editor Area */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Video Player Column */}
        <div className="lg:col-span-2 space-y-4">
          {/* Video Player */}
          <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="relative bg-black aspect-video">
              {videoError && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900/90 z-10">
                  <div className="text-center">
                    <AlertCircle className="h-10 w-10 mx-auto mb-2 text-red-500" />
                    <p className="text-red-400">{videoError}</p>
                    <p className="text-xs text-gray-500 mt-1">URL: {getPlayableVideoUrl(videoData)?.substring(0, 60)}...</p>
                  </div>
                </div>
              )}
              <video
                ref={videoRef}
                className="w-full h-full"
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onError={(e) => {
                  console.error('[AdCueEditor] Video error:', e);
                  setVideoError('Failed to load video');
                }}
                playsInline
              />
              
              {/* Cue Point Markers on Video */}
              {sortedCuePoints.map((cue, idx) => {
                const leftPercent = (cue.timestamp / duration) * 100;
                return (
                  <div
                    key={cue.id}
                    className="absolute bottom-0 w-1 h-4 bg-yellow-500 cursor-pointer hover:bg-yellow-400 transition-colors"
                    style={{ left: `${leftPercent}%` }}
                    onClick={() => seekTo(cue.timestamp)}
                    title={`Ad at ${formatTime(cue.timestamp)}`}
                  />
                );
              })}
            </div>
            
            {/* Video Controls */}
            <div className="p-4 space-y-3">
              {/* Timeline */}
              <div 
                ref={timelineRef}
                className="relative h-3 bg-gray-800 rounded-full cursor-pointer"
                onClick={handleTimelineClick}
              >
                {/* Progress bar */}
                <div 
                  className="absolute top-0 left-0 h-full bg-blue-500 rounded-full"
                  style={{ width: `${(currentTime / duration) * 100}%` }}
                />
                
                {/* Cue point markers on timeline */}
                {sortedCuePoints.map((cue) => {
                  const leftPercent = (cue.timestamp / duration) * 100;
                  return (
                    <div
                      key={cue.id}
                      className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-yellow-500 rounded-full border-2 border-yellow-300 cursor-pointer hover:scale-125 transition-transform z-10"
                      style={{ left: `calc(${leftPercent}% - 6px)` }}
                      onClick={(e) => { e.stopPropagation(); seekTo(cue.timestamp); }}
                      title={`${cue.label || 'Ad'} - ${formatTime(cue.timestamp)}`}
                    />
                  );
                })}
                
                {/* Pre-roll marker */}
                {adSettings?.pre_roll_enabled && (
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-green-500 rounded-full border-2 border-green-300 z-10"
                    style={{ left: '-6px' }}
                    title="Pre-roll Ad"
                  />
                )}
                
                {/* Post-roll marker */}
                {adSettings?.post_roll_enabled && (
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-red-500 rounded-full border-2 border-red-300 z-10"
                    style={{ right: '-6px' }}
                    title="Post-roll Ad"
                  />
                )}
              </div>
              
              {/* Control buttons */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => skip(-10)} className="text-gray-400">
                    <SkipBack className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={togglePlay}
                    className="text-white bg-blue-600 hover:bg-blue-500 rounded-full w-10 h-10"
                  >
                    {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => skip(10)} className="text-gray-400">
                    <SkipForward className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={toggleMute} className="text-gray-400">
                    {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                  </Button>
                  <span className="text-sm text-gray-400 ml-2">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </div>
                
                <Button 
                  onClick={addCueAtCurrentTime}
                  className="bg-yellow-600 hover:bg-yellow-500 text-black font-medium"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Ad at {formatTime(currentTime)}
                </Button>
              </div>
            </div>
          </div>
          
          {/* Cue Points List */}
          <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="p-4 border-b border-gray-800">
              <h3 className="font-bold text-white flex items-center gap-2">
                <Target className="h-5 w-5 text-yellow-400" />
                Mid-Roll Ad Cue Points
              </h3>
              <p className="text-xs text-gray-500 mt-1">Click on the timeline or video to seek, then click "Add Ad" to insert an ad at that point</p>
            </div>
            <div className="p-4">
              {sortedCuePoints.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Target className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p className="text-gray-400">No ad cue points yet</p>
                  <p className="text-xs mt-1">Play the video and click "Add Ad" to insert ad breaks</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {sortedCuePoints.map((cue, idx) => (
                    <div 
                      key={cue.id} 
                      className="flex items-center justify-between bg-gray-800/50 rounded-lg p-3 hover:bg-gray-800 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => seekTo(cue.timestamp)}
                          className="w-8 h-8 rounded-full bg-yellow-600/20 text-yellow-400 flex items-center justify-center hover:bg-yellow-600/30"
                        >
                          <Play className="h-4 w-4 ml-0.5" />
                        </button>
                        <div>
                          <p className="text-sm font-medium text-white">{cue.label || `Ad ${idx + 1}`}</p>
                          <p className="text-xs text-gray-400">
                            <Clock className="h-3 w-3 inline mr-1" />
                            {formatTime(cue.timestamp)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Badge className={`${cue.ad_type === 'google' ? 'bg-blue-600' : 'bg-purple-600'} text-white text-xs`}>
                          {cue.ad_type === 'google' ? 'Google AdSense' : 'Custom Ad'}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteCuePoint(cue.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Settings Column */}
        <div className="space-y-4">
          {/* Pre-Roll Settings */}
          <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="p-4 border-b border-gray-800">
              <h3 className="font-bold text-white flex items-center gap-2">
                <Play className="h-5 w-5 text-green-400" />
                Pre-Roll Ad
              </h3>
              <p className="text-xs text-gray-500 mt-1">Ad shown before video starts</p>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-300">Enable Pre-Roll</span>
                <Switch
                  checked={adSettings?.pre_roll_enabled || false}
                  onCheckedChange={(checked) => updateSettings({ pre_roll_enabled: checked })}
                />
              </div>
              
              {adSettings?.pre_roll_enabled && (
                <>
                  <div>
                    <label className="text-xs text-gray-400 mb-2 block">Ad Type</label>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant={adSettings?.pre_roll_ad_type === 'google' ? 'default' : 'outline'}
                        onClick={() => updateSettings({ pre_roll_ad_type: 'google' })}
                        className={adSettings?.pre_roll_ad_type === 'google' ? 'bg-blue-600' : 'border-gray-700 text-gray-300'}
                      >
                        Google AdSense
                      </Button>
                      <Button
                        size="sm"
                        variant={adSettings?.pre_roll_ad_type === 'custom' ? 'default' : 'outline'}
                        onClick={() => updateSettings({ pre_roll_ad_type: 'custom' })}
                        className={adSettings?.pre_roll_ad_type === 'custom' ? 'bg-purple-600' : 'border-gray-700 text-gray-300'}
                      >
                        Custom Ad
                      </Button>
                    </div>
                  </div>
                  
                  {adSettings?.pre_roll_ad_type === 'custom' && (
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-gray-400 mb-2 block">Select Ad Video</label>
                        {availableAds.length > 0 ? (
                          <select
                            value={adSettings?.pre_roll_custom_url || ''}
                            onChange={(e) => {
                              const selectedAd = availableAds.find(ad => ad.video_url === e.target.value);
                              updateSettings({ 
                                pre_roll_custom_url: e.target.value,
                                pre_roll_custom_ad_id: selectedAd?.id || '',
                                pre_roll_skip_after: selectedAd?.skip_after ?? 5,
                                pre_roll_cta_enabled: selectedAd?.cta_enabled ?? false,
                                pre_roll_cta_text: selectedAd?.cta_text || 'Learn More',
                                pre_roll_click_url: selectedAd?.click_url || ''
                              });
                            }}
                            className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="">-- Select an ad video --</option>
                            {availableAds.map((ad) => (
                              <option key={ad.id} value={ad.video_url}>
                                {ad.name} ({Math.floor(ad.duration / 60)}:{String(ad.duration % 60).padStart(2, '0')})
                              </option>
                            ))}
                          </select>
                        ) : (
                          <p className="text-xs text-gray-500">No ads uploaded. Go to Video Ads tab to upload ads.</p>
                        )}
                      </div>
                      
                      {adSettings?.pre_roll_custom_url && (
                        <div className="space-y-3 border-t border-gray-700 pt-3">
                          <div>
                            <label className="text-xs text-gray-400 mb-1 block">Skip After (seconds)</label>
                            <Input
                              type="number"
                              min="0"
                              value={adSettings?.pre_roll_skip_after ?? 5}
                              onChange={(e) => updateSettings({ pre_roll_skip_after: parseInt(e.target.value) || 0 })}
                              className="bg-gray-800 border-gray-700 text-white h-8 text-sm"
                            />
                            <p className="text-xs text-gray-500 mt-1">0 = not skippable</p>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-400">Enable CTA Button</span>
                            <Switch
                              checked={adSettings?.pre_roll_cta_enabled || false}
                              onCheckedChange={(checked) => updateSettings({ pre_roll_cta_enabled: checked })}
                            />
                          </div>
                          {adSettings?.pre_roll_cta_enabled && (
                            <>
                              <div>
                                <label className="text-xs text-gray-400 mb-1 block">CTA Text</label>
                                <Input
                                  value={adSettings?.pre_roll_cta_text || 'Learn More'}
                                  onChange={(e) => updateSettings({ pre_roll_cta_text: e.target.value })}
                                  className="bg-gray-800 border-gray-700 text-white h-8 text-sm"
                                  placeholder="Learn More"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-gray-400 mb-1 block">CTA Link URL</label>
                                <Input
                                  value={adSettings?.pre_roll_click_url || ''}
                                  onChange={(e) => updateSettings({ pre_roll_click_url: e.target.value })}
                                  className="bg-gray-800 border-gray-700 text-white h-8 text-sm"
                                  placeholder="https://..."
                                />
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          
          {/* Post-Roll Settings */}
          <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="p-4 border-b border-gray-800">
              <h3 className="font-bold text-white flex items-center gap-2">
                <Clock className="h-5 w-5 text-red-400" />
                Post-Roll Ad
              </h3>
              <p className="text-xs text-gray-500 mt-1">Ad shown after video ends</p>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-300">Enable Post-Roll</span>
                <Switch
                  checked={adSettings?.post_roll_enabled || false}
                  onCheckedChange={(checked) => updateSettings({ post_roll_enabled: checked })}
                />
              </div>
              
              {adSettings?.post_roll_enabled && (
                <>
                  <div>
                    <label className="text-xs text-gray-400 mb-2 block">Ad Type</label>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant={adSettings?.post_roll_ad_type === 'google' ? 'default' : 'outline'}
                        onClick={() => updateSettings({ post_roll_ad_type: 'google' })}
                        className={adSettings?.post_roll_ad_type === 'google' ? 'bg-blue-600' : 'border-gray-700 text-gray-300'}
                      >
                        Google AdSense
                      </Button>
                      <Button
                        size="sm"
                        variant={adSettings?.post_roll_ad_type === 'custom' ? 'default' : 'outline'}
                        onClick={() => updateSettings({ post_roll_ad_type: 'custom' })}
                        className={adSettings?.post_roll_ad_type === 'custom' ? 'bg-purple-600' : 'border-gray-700 text-gray-300'}
                      >
                        Custom Ad
                      </Button>
                    </div>
                  </div>
                  
                  {adSettings?.post_roll_ad_type === 'custom' && (
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-gray-400 mb-2 block">Select Ad Video</label>
                        {availableAds.length > 0 ? (
                          <select
                            value={adSettings?.post_roll_custom_url || ''}
                            onChange={(e) => {
                              const selectedAd = availableAds.find(ad => ad.video_url === e.target.value);
                              updateSettings({ 
                                post_roll_custom_url: e.target.value,
                                post_roll_custom_ad_id: selectedAd?.id || '',
                                post_roll_skip_after: selectedAd?.skip_after ?? 5,
                                post_roll_cta_enabled: selectedAd?.cta_enabled ?? false,
                                post_roll_cta_text: selectedAd?.cta_text || 'Learn More',
                                post_roll_click_url: selectedAd?.click_url || ''
                              });
                            }}
                            className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="">-- Select an ad video --</option>
                            {availableAds.map((ad) => (
                              <option key={ad.id} value={ad.video_url}>
                                {ad.name} ({Math.floor(ad.duration / 60)}:{String(ad.duration % 60).padStart(2, '0')})
                              </option>
                            ))}
                          </select>
                        ) : (
                          <p className="text-xs text-gray-500">No ads uploaded. Go to Video Ads tab to upload ads.</p>
                        )}
                      </div>
                      
                      {adSettings?.post_roll_custom_url && (
                        <div className="space-y-3 border-t border-gray-700 pt-3">
                          <div>
                            <label className="text-xs text-gray-400 mb-1 block">Skip After (seconds)</label>
                            <Input
                              type="number"
                              min="0"
                              value={adSettings?.post_roll_skip_after ?? 5}
                              onChange={(e) => updateSettings({ post_roll_skip_after: parseInt(e.target.value) || 0 })}
                              className="bg-gray-800 border-gray-700 text-white h-8 text-sm"
                            />
                            <p className="text-xs text-gray-500 mt-1">0 = not skippable</p>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-400">Enable CTA Button</span>
                            <Switch
                              checked={adSettings?.post_roll_cta_enabled || false}
                              onCheckedChange={(checked) => updateSettings({ post_roll_cta_enabled: checked })}
                            />
                          </div>
                          {adSettings?.post_roll_cta_enabled && (
                            <>
                              <div>
                                <label className="text-xs text-gray-400 mb-1 block">CTA Text</label>
                                <Input
                                  value={adSettings?.post_roll_cta_text || 'Learn More'}
                                  onChange={(e) => updateSettings({ post_roll_cta_text: e.target.value })}
                                  className="bg-gray-800 border-gray-700 text-white h-8 text-sm"
                                  placeholder="Learn More"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-gray-400 mb-1 block">CTA Link URL</label>
                                <Input
                                  value={adSettings?.post_roll_click_url || ''}
                                  onChange={(e) => updateSettings({ post_roll_click_url: e.target.value })}
                                  className="bg-gray-800 border-gray-700 text-white h-8 text-sm"
                                  placeholder="https://..."
                                />
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          
          {/* Mid-Roll Mode */}
          <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="p-4 border-b border-gray-800">
              <h3 className="font-bold text-white flex items-center gap-2">
                <Settings className="h-5 w-5 text-yellow-400" />
                Mid-Roll Mode
              </h3>
              <p className="text-xs text-gray-500 mt-1">How mid-roll ads are placed</p>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={adSettings?.mid_roll_mode === 'custom' ? 'default' : 'outline'}
                  onClick={() => updateSettings({ mid_roll_mode: 'custom' })}
                  className={`flex-1 ${adSettings?.mid_roll_mode === 'custom' ? 'bg-yellow-600 text-black' : 'border-gray-700 text-gray-300'}`}
                >
                  Custom
                </Button>
                <Button
                  size="sm"
                  variant={adSettings?.mid_roll_mode === 'automatic' ? 'default' : 'outline'}
                  onClick={() => updateSettings({ mid_roll_mode: 'automatic' })}
                  className={`flex-1 ${adSettings?.mid_roll_mode === 'automatic' ? 'bg-yellow-600 text-black' : 'border-gray-700 text-gray-300'}`}
                >
                  Automatic
                </Button>
              </div>
              
              {adSettings?.mid_roll_mode === 'custom' && (
                <p className="text-xs text-gray-400">
                  You manually select where ads appear using the timeline above.
                </p>
              )}
              
              {adSettings?.mid_roll_mode === 'automatic' && (
                <div>
                  <label className="text-xs text-gray-400 mb-2 block">Ad Interval (seconds)</label>
                  <Input
                    type="number"
                    min={60}
                    max={1800}
                    value={adSettings?.automatic_interval || 300}
                    onChange={(e) => updateSettings({ automatic_interval: parseInt(e.target.value) })}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Ads will be inserted every {Math.floor((adSettings?.automatic_interval || 300) / 60)} minutes
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {/* Summary */}
          <div className="bg-gray-800/50 rounded-xl p-4">
            <h4 className="font-medium text-white mb-3">Ad Summary</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Pre-Roll</span>
                <span className={adSettings?.pre_roll_enabled ? 'text-green-400' : 'text-gray-500'}>
                  {adSettings?.pre_roll_enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Mid-Rolls</span>
                <span className="text-yellow-400">{sortedCuePoints.length} cue points</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Post-Roll</span>
                <span className={adSettings?.post_roll_enabled ? 'text-red-400' : 'text-gray-500'}>
                  {adSettings?.post_roll_enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Add Cue Point Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-xl border border-gray-800 p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-white flex items-center gap-2">
                <Plus className="h-5 w-5 text-yellow-400" />
                Add Ad Cue Point
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)} className="text-gray-400">
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Timestamp</label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.1"
                    value={newCue.timestamp}
                    onChange={(e) => setNewCue({ ...newCue, timestamp: parseFloat(e.target.value) })}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                  <span className="text-gray-400 text-sm">{formatTime(newCue.timestamp)}</span>
                </div>
              </div>
              
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Ad Type</label>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={newCue.ad_type === 'google' ? 'default' : 'outline'}
                    onClick={() => setNewCue({ ...newCue, ad_type: 'google' })}
                    className={`flex-1 ${newCue.ad_type === 'google' ? 'bg-blue-600' : 'border-gray-700 text-gray-300'}`}
                  >
                    <DollarSign className="h-4 w-4 mr-1" />
                    Google AdSense
                  </Button>
                  <Button
                    size="sm"
                    variant={newCue.ad_type === 'custom' ? 'default' : 'outline'}
                    onClick={() => setNewCue({ ...newCue, ad_type: 'custom' })}
                    className={`flex-1 ${newCue.ad_type === 'custom' ? 'bg-purple-600' : 'border-gray-700 text-gray-300'}`}
                  >
                    <Link2 className="h-4 w-4 mr-1" />
                    Custom Ad
                  </Button>
                </div>
              </div>
              
              {newCue.ad_type === 'custom' && (
                <>
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Select Ad Video</label>
                    {availableAds.length > 0 ? (
                      <select
                        value={newCue.custom_ad_id || ''}
                        onChange={(e) => {
                          const selectedAd = availableAds.find(ad => ad.id === e.target.value);
                          console.log('[AdCuePointEditor] Selected ad:', selectedAd);
                          setNewCue({ 
                            ...newCue, 
                            custom_ad_id: e.target.value,
                            custom_ad_url: selectedAd?.video_url || '',
                            custom_ad_duration: selectedAd?.duration || 15,
                            // Copy CTA settings from the selected ad
                            skip_after: selectedAd?.skip_after ?? 5,
                            cta_enabled: selectedAd?.cta_enabled ?? false,
                            cta_text: selectedAd?.cta_text || 'Learn More',
                            click_url: selectedAd?.click_url || ''
                          });
                        }}
                        className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">-- Select an ad video --</option>
                        {availableAds.map((ad) => (
                          <option key={ad.id} value={ad.id}>
                            {ad.name} ({Math.floor(ad.duration / 60)}:{String(ad.duration % 60).padStart(2, '0')})
                          </option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-sm text-gray-500">No ads uploaded. Go to Video Ads tab to upload ads.</p>
                    )}
                  </div>
                  {newCue.custom_ad_id && (
                    <div className="bg-gray-800 rounded-lg p-3 flex items-center gap-3">
                      {availableAds.find(ad => ad.id === newCue.custom_ad_id)?.thumbnail_url && (
                        <img 
                          src={availableAds.find(ad => ad.id === newCue.custom_ad_id)?.thumbnail_url}
                          alt="Ad preview"
                          className="w-16 h-10 rounded object-cover"
                        />
                      )}
                      <div className="flex-1">
                        <p className="text-sm text-white">{availableAds.find(ad => ad.id === newCue.custom_ad_id)?.name}</p>
                        <p className="text-xs text-gray-400">Duration: {newCue.custom_ad_duration}s</p>
                      </div>
                    </div>
                  )}
                </>
              )}
              
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 border-gray-700 text-gray-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveCuePoint}
                  disabled={saving}
                  className="flex-1 bg-yellow-600 hover:bg-yellow-500 text-black"
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                  Add Cue Point
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdCuePointEditor;
